import React, { useState } from 'react';
import {
	Stack,
	Typography,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Fade,
	IconButton,
	CircularProgress,
	Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Edit, Delete } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useDeleteTaskMutation } from '@/features/engagements/engagementsApi';
import { useInstantSearchDataGridContext } from '@/components/tables/InstantSearchDataGrid';
import { openDialog } from '@/features/dialog/dialogSlice';
import FolioLink from '@/components/FolioLink';
import {
	getSearchRecordNavigateOptions,
	getSearchRecordPath,
} from '@/components/tables/InstantSearchDataGrid/data';

const TaskMenu = ({ anchorEl, onClose, onEdit, onDelete }) => {
	const open = Boolean(anchorEl);

	const handleEdit = () => {
		onEdit?.();
		onClose();
	};

	const handleDelete = () => {
		onDelete?.();
		onClose();
	};

	return (
		<Menu open={open} onClose={onClose} anchorEl={anchorEl}>
			<MenuItem onClick={handleEdit}>
				<ListItemIcon>
					<Edit fontSize='small' />
				</ListItemIcon>
				<ListItemText>Edit task</ListItemText>
			</MenuItem>
			<MenuItem onClick={handleDelete}>
				<ListItemIcon>
					<Delete fontSize='small' />
				</ListItemIcon>
				<ListItemText>Delete task</ListItemText>
			</MenuItem>
		</Menu>
	);
};

export const DataGridTaskName = ({ row }) => {
	const dispatch = useDispatch();
	const { hoveredRowId } = useInstantSearchDataGridContext();
	const [anchorEl, setAnchorEl] = useState(null);
	const [_updateTask, { isLoading }] = useDeleteTaskMutation({ fixedCacheKey: row.id });
	const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation({
		fixedCacheKey: row.id,
	});

	const { subject, completed = false, id, dealId, clientId, objectType } = row || {};

	const showMenu = hoveredRowId === row?.id;
	const taskPath = getSearchRecordPath(row);
	const taskLinkState = getSearchRecordNavigateOptions(row)?.state;
	const taskSubjectText = (
		<Tooltip title={subject ?? ''} disableInteractive>
			<Typography
				sx={{
					color: 'primary.main',
					fontWeight: 600,
					'&:hover': {
						textDecoration: 'underline',
					},
					...(completed && { textDecoration: 'line-through' }),
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{subject ?? ''}
			</Typography>
		</Tooltip>
	);

	const handleOpenMenu = (e) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleEdit = () => {
		handleMenuClose();
		dispatch(openDialog({ dialogName: 'task', data: { task: row } }));
	};

	const handleDelete = async () => {
		if (id) {
			await deleteTask({
				dealId,
				clientId,
				taskId: id,
				objectType,
			}).unwrap();
		}
		handleMenuClose();
	};

	return (
		<Stack
			direction='row'
			spacing={2}
			alignItems='center'
			justifyContent='space-between'
			width='100%'
		>
			{taskPath ? (
				<FolioLink
					to={taskPath}
					state={taskLinkState}
					stopPropagation
					sx={{ minWidth: 0, overflow: 'hidden' }}
				>
					{taskSubjectText}
				</FolioLink>
			) : (
				taskSubjectText
			)}

			<Fade in={showMenu} orientation='horizontal'>
				<IconButton
					onClick={handleOpenMenu}
					disabled={isLoading || isDeleting}
					size='small'
					sx={{ padding: '4px' }}
				>
					<MoreVertIcon fontSize='small' />
				</IconButton>
			</Fade>

			<TaskMenu
				anchorEl={anchorEl}
				onClose={handleMenuClose}
				onEdit={handleEdit}
				onDelete={handleDelete}
			/>
		</Stack>
	);
};
